import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ColumnDef } from "@tanstack/react-table";
import {
  markAmcReadyForActivation,
  completeAmc,
  addExtraAmcVisit,
  cancelAmc,
  deleteAmc,
  getAmcContracts,
  regenerateAmcInvoice,
} from "features/Amc/services/AmcService";
import RegenerateInvoiceModal from "components/common/RegenerateInvoiceModal";
import {
  Amc,
  type AddExtraAmcVisitRequest,
} from "features/Amc/types/amc.types";
import { AmcContractRules, canActOnAmcContract } from "features/Amc/rules";
import { CodeTitleColumns, IndexColumn } from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import DeleteModal from "components/common/DeleteModal";
import {
  AmcContractStatus,
  AmcSourceType,
  amcAllowsEditPageAccess,
  amcStatusMap,
} from "features/Amc/constants/amc-contract.constants";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  UncontrolledDropdown,
} from "reactstrap";
import ConfirmActionModal from "components/common/ConfirmActionModal";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { useProfile } from "features/Authentication/hooks/useProfile";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import AddExtraAmcVisitModal from "features/Amc/components/AddExtraAmcVisitModal";
import WorkAssignmentForm from "features/WorkAssignments/WorkAssignmentForm";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import AmcAssigneesAvatarGroup from "features/Amc/components/AmcAssigneesAvatarGroup";
import AmcListFilters, {
  amcListFiltersToListParams,
  countAmcListFiltersActive,
  emptyAmcListFilters,
  type AmcListFiltersState,
} from "./AmcListFilters";
import { formatCurrency } from "utils/currency";
import LinkedClientAdvancesTable from "components/common/LinkedClientAdvancesTable";
import {
  ClientAdvanceSourceType,
  ClientAdvanceStatus,
} from "features/ClientAdvances/constants/client-advance.constants";
import type { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";

function isTerminalAmcStatus(status: string | null | undefined): boolean {
  const s = String(status ?? "")
    .trim()
    .toUpperCase();
  return s === AmcContractStatus.CANCELLED || s === AmcContractStatus.COMPLETED;
}

function axiosMessage(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ??
    (err as Error)?.message ??
    fallback
  );
}

function contractDuration(
  start: string | undefined,
  end: string | undefined,
): string {
  if (!start || !end) return "";
  const s = new Date(start);
  const e = new Date(end);
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return "";
  let months =
    (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) months--;
  if (months >= 1) return `${months} month${months !== 1 ? "s" : ""}`;
  const days = Math.round((e.getTime() - s.getTime()) / 86_400_000);
  return `${days} day${days !== 1 ? "s" : ""}`;
}

export const ContractsTable = () => {
  const { can } = useAccess();
  const { isYou, isSuperAdmin } = useProfile();
  const canReadAmc = can(Permissions.AMC.READ);
  const canUpdateAmc = can(Permissions.AMC.UPDATE);
  const canMarkAmcReadyForActivation = can(
    Permissions.AMC.READY_FOR_ACTIVATION,
  );
  const canCancelAmc = can(Permissions.AMC.CANCEL);
  const canCompleteAmc = can(Permissions.AMC.COMPLETE);
  const canRegenerateInvoiceAmc = can(Permissions.AMC.REGENERATE_INVOICE);
  const canDeleteAmc = can(Permissions.AMC.DELETE);
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);
  const tableRef = useRef<() => void>(null);
  const [contract, setContract] = useState<Amc>();
  const [approveModal, setApproveModal] = useState<boolean>(false);
  const [approveFinalAmount, setApproveFinalAmount] = useState("");
  const [approveDraftAdvanceCount, setApproveDraftAdvanceCount] = useState(0);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [cancelReason, setCancelReason] = useState("");
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [completeModal, setCompleteModal] = useState(false);
  const [completeSubmitting, setCompleteSubmitting] = useState(false);
  const [regenerateModal, setRegenerateModal] = useState(false);
  const [extraVisitModal, setExtraVisitModal] = useState<boolean>(false);
  const [extraVisitSubmitting, setExtraVisitSubmitting] = useState(false);
  const [assignFormOpen, setAssignFormOpen] = useState(false);
  const [assignFormContract, setAssignFormContract] = useState<
    Amc | undefined
  >();
  const [assignFormIsReassign, setAssignFormIsReassign] = useState(false);
  const [listFilters, setListFilters] = useState<AmcListFiltersState>(() =>
    emptyAmcListFilters(),
  );

  const listRequestParams = useMemo(
    () => amcListFiltersToListParams(listFilters),
    [listFilters],
  );

  const filtersBadgeCount = useMemo(
    () => countAmcListFiltersActive(listFilters),
    [listFilters],
  );

  const clearListFilters = useCallback(() => {
    setListFilters(emptyAmcListFilters());
  }, []);

  const onClickAssign = useCallback((record: Amc) => {
    setAssignFormContract(record);
    setAssignFormIsReassign(false);
    setAssignFormOpen(true);
  }, []);

  const onClickReassign = useCallback((record: Amc) => {
    setAssignFormContract(record);
    setAssignFormIsReassign(true);
    setAssignFormOpen(true);
  }, []);

  const closeAssignmentForm = useCallback(() => {
    setAssignFormOpen(false);
    setAssignFormIsReassign(false);
  }, []);

  const onClickApprove = useCallback((record: Amc) => {
    setContract(record);
    setApproveFinalAmount("");
    setApproveDraftAdvanceCount(0);
    setApproveModal(true);
  }, []);

  const onClickCancel = useCallback((record: Amc) => {
    setContract(record);
    setCancelReason("");
    setCancelModal(true);
  }, []);

  const onClickExtraVisit = useCallback((record: Amc) => {
    setContract(record);
    setExtraVisitModal(true);
  }, []);

  const onClickComplete = useCallback((record: Amc) => {
    setContract(record);
    setCompleteModal(true);
  }, []);

  const onClickRegenerateInvoice = useCallback((record: Amc) => {
    setContract(record);
    setRegenerateModal(true);
  }, []);

  const onClickDelete = useCallback((record: Amc) => {
    setContract(record);
    setDeleteModal(true);
  }, []);

  const handleDelete = async () => {
    if (contract) {
      try {
        const response = await deleteAmc(contract.id);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setDeleteModal(false);
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not delete contract",
        );
      }
    }
  };

  const handleCancel = async () => {
    if (contract) {
      try {
        const response = await cancelAmc(contract.id, cancelReason);
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
        }
        setCancelModal(false);
        setCancelReason("");
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not cancel contract",
        );
      }
    }
  };

  const handleApprove = async () => {
    if (contract) {
      try {
        const response = await markAmcReadyForActivation(contract.id, {
          finalAmount:
            approveFinalAmount.trim() !== ""
              ? Number(approveFinalAmount)
              : null,
        });
        if (response.success) {
          toast.success(response.message);
          tableRef.current?.();
          setApproveModal(false);
        } else {
          toast.error(
            response.message ?? "Could not mark contract ready for activation",
          );
        }
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not mark contract ready for activation",
        );
      }
    }
  };

  const handleComplete = async () => {
    if (!contract) return;
    setCompleteSubmitting(true);
    try {
      const res = await completeAmc(contract.id);
      if (res.success) {
        toast.success(res.message ?? "Contract marked as completed.");
        setCompleteModal(false);
        tableRef.current?.();
      } else {
        toast.error(res.message ?? "Could not complete this contract.");
      }
    } catch (err: unknown) {
      toast.error(axiosMessage(err, "Could not complete this contract."));
    } finally {
      setCompleteSubmitting(false);
    }
  };

  const handleAddExtraVisit = async (body: AddExtraAmcVisitRequest) => {
    const contractId = contract?.id;
    if (contractId == null || !Number.isInteger(contractId) || contractId <= 0)
      return;
    setExtraVisitSubmitting(true);
    try {
      const response = await addExtraAmcVisit(contractId, body);
      if (response.success) {
        toast.success(response.message);
        setExtraVisitModal(false);
        tableRef.current?.();
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not add extra visit",
      );
    } finally {
      setExtraVisitSubmitting(false);
    }
  };

  const columns: ColumnDef<Amc>[] = useMemo(
    () => [
      IndexColumn<Amc>(),
      ...CodeTitleColumns<Amc>({
        titleHeader: "AMC",
        accessorKey: "code",
        resolve: (r) => ({
          code: r.code,
          title: r.title?.trim(),
          to: canReadAmc ? `/amc-management/${r.id}` : null,
        }),
      }),

      {
        header: "Client",
        accessorKey: "clientId",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const r = row.original;
          const name =
            r.client != null
              ? r.client.name?.trim() || r.client.company?.trim() || ""
              : "";
          return r.clientId != null && name ? (
            <Link to={`/clients/${r.clientId}`} className="link fw-medium">
              {name}
            </Link>
          ) : (
            "—"
          );
        },
      },
      {
        header: "Period",
        id: "period",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { startDate, endDate } = row.original;
          const duration = contractDuration(startDate, endDate);
          return (
            <div className="d-flex flex-column gap-1">
              <div className="d-flex align-items-center gap-1 text-nowrap">
                <DateOnly date={startDate} />
                <span className="text-muted">→</span>
                <DateOnly date={endDate} />
              </div>
              {duration ? (
                <span
                  className="badge bg-light text-muted fw-normal"
                  style={{ width: "fit-content" }}
                >
                  {duration}
                </span>
              ) : null}
            </div>
          );
        },
      },

      {
        header: "Assigned",
        id: "assigned",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const isTerminal = isTerminalAmcStatus(record.status);
          return (
            <AmcAssigneesAvatarGroup
              contract={record}
              workAssignments={record.workAssignments}
              canReadDetail={canReadAmc}
              canAssignMore={canCreateAssignment && !isTerminal}
              onOpenAssign={onClickAssign}
            />
          );
        },
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const val = row.original.status;
          return <StatusBadge value={val} map={amcStatusMap} />;
        },
      },

      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ getValue }) => {
          const d = getValue() as string | undefined;
          return d ? (
            <DateTime date={d} />
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const canActOnContract = canActOnAmcContract(
            record,
            isYou,
            isSuperAdmin,
          );
          const showView = canReadAmc;
          const showEdit =
            canUpdateAmc &&
            amcAllowsEditPageAccess(record.status, isSuperAdmin);
          // Ready-for-activation is permission-gated only — no assignment requirement.
          const showApprove = canMarkAmcReadyForActivation;
          const showCancel = canCancelAmc && canActOnContract;
          const showExtraVisit = canUpdateAmc;
          const showAssign =
            (canCreateAssignment || canUpdateAssignment) &&
            !isTerminalAmcStatus(record.status);
          const showComplete =
            canCompleteAmc &&
            canActOnContract &&
            AmcContractRules.canComplete(record.status as AmcContractStatus);
          const showRegenerateInvoice =
            canRegenerateInvoiceAmc &&
            AmcContractRules.canRegenerateInvoice(
              record.status as AmcContractStatus,
            );
          const showDelete =
            canDeleteAmc &&
            record.status === AmcContractStatus.DRAFT &&
            record.sourceType !== AmcSourceType.QUOTATION;
          if (
            !showView &&
            !showEdit &&
            !showApprove &&
            !showCancel &&
            !showExtraVisit &&
            !showAssign &&
            !showComplete &&
            !showRegenerateInvoice &&
            !showDelete
          ) {
            return <span className="text-muted small">—</span>;
          }
          const canAddExtraVisit =
            showExtraVisit && record.status === AmcContractStatus.ACTIVE;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Contract actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                {showView ? (
                  <DropdownItem tag={Link} to={`/amc-management/${record.id}`}>
                    <i className="ri-eye-line align-bottom me-2 text-muted" />
                    View
                  </DropdownItem>
                ) : null}
                {showView ? (
                  <DropdownItem
                    tag={Link}
                    to={`/amc-management/${record.id}?tab=visits`}
                  >
                    <i className="ri-calendar-check-line align-bottom me-2 text-muted" />
                    Visits
                  </DropdownItem>
                ) : null}
                {showEdit ? (
                  <DropdownItem
                    tag={Link}
                    to={`/amc-management/edit/${record.id}`}
                  >
                    <i className="ri-pencil-line align-bottom me-2 text-muted" />
                    Edit
                  </DropdownItem>
                ) : null}
                {showAssign ? (
                  <>
                    {showView || showEdit ? <DropdownItem divider /> : null}
                    {canCreateAssignment ? (
                      <DropdownItem onClick={() => onClickAssign(record)}>
                        <i className="ri-user-follow-line align-bottom me-2 text-muted" />
                        Assign user
                      </DropdownItem>
                    ) : null}
                    {canUpdateAssignment ? (
                      <DropdownItem onClick={() => onClickReassign(record)}>
                        <i className="ri-user-shared-line align-bottom me-2 text-muted" />
                        Reassign
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
                {showApprove ? (
                  <DropdownItem
                    onClick={() => onClickApprove(record)}
                    disabled={
                      !AmcContractRules.canMarkReadyForActivation(
                        record.status as AmcContractStatus,
                      )
                    }
                  >
                    <i className="ri-check-double-line align-bottom me-2 text-muted" />
                    Mark Ready for Activation
                  </DropdownItem>
                ) : null}
                {showCancel ? (
                  <DropdownItem
                    onClick={() => onClickCancel(record)}
                    disabled={
                      !AmcContractRules.canCancel(
                        record.status as AmcContractStatus,
                      )
                    }
                  >
                    <i className="ri-close-circle-line align-bottom me-2 text-muted" />
                    Cancel
                  </DropdownItem>
                ) : null}
                {showComplete ? (
                  <DropdownItem onClick={() => onClickComplete(record)}>
                    <i className="ri-checkbox-circle-line align-bottom me-2 text-success" />
                    Complete
                  </DropdownItem>
                ) : null}
                {showRegenerateInvoice ? (
                  <DropdownItem
                    onClick={() => onClickRegenerateInvoice(record)}
                  >
                    <i className="ri-refresh-line align-bottom me-2 text-muted" />
                    Regenerate invoice
                  </DropdownItem>
                ) : null}
                {showExtraVisit ? (
                  <>
                    {showView ||
                    showEdit ||
                    showApprove ||
                    showCancel ||
                    showComplete ||
                    showRegenerateInvoice ? (
                      <DropdownItem divider />
                    ) : null}
                    <DropdownItem
                      onClick={() => onClickExtraVisit(record)}
                      disabled={!canAddExtraVisit}
                    >
                      <i className="ri-add-circle-line align-bottom me-2 text-muted" />
                      Extra visit
                    </DropdownItem>
                  </>
                ) : null}
                {showDelete ? (
                  <>
                    {showView ||
                    showEdit ||
                    showApprove ||
                    showCancel ||
                    showComplete ||
                    showExtraVisit ? (
                      <DropdownItem divider />
                    ) : null}
                    <DropdownItem
                      className="text-danger"
                      onClick={() => onClickDelete(record)}
                    >
                      <i className="ri-delete-bin-line align-bottom me-2" />
                      Delete
                    </DropdownItem>
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      onClickApprove,
      onClickCancel,
      onClickExtraVisit,
      onClickDelete,
      onClickAssign,
      onClickReassign,
      onClickComplete,
      canReadAmc,
      canUpdateAmc,
      canMarkAmcReadyForActivation,
      canCancelAmc,
      canCompleteAmc,
      canDeleteAmc,
      canCreateAssignment,
      canUpdateAssignment,
    ],
  );

  return (
    <React.Fragment>
      <WorkAssignmentForm
        show={assignFormOpen}
        sourceType={WorkAssignmentSourceType.AMC}
        sourceId={assignFormContract?.id ?? 0}
        sourceLabel={
          assignFormContract?.code
            ? `AMC ${assignFormContract.code}`
            : assignFormContract?.title?.trim()
              ? `AMC: ${assignFormContract.title}`
              : `AMC #${assignFormContract?.id ?? ""}`
        }
        isReassign={assignFormIsReassign}
        onCloseClick={closeAssignmentForm}
        onSuccess={() => {
          closeAssignmentForm();
          tableRef.current?.();
        }}
      />

      <AddExtraAmcVisitModal
        isOpen={extraVisitModal}
        toggle={() => setExtraVisitModal(false)}
        contractLabel={contract?.code ?? null}
        submitting={extraVisitSubmitting}
        onConfirm={handleAddExtraVisit}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={() => void handleDelete()}
        onCloseClick={() => setDeleteModal(false)}
      />

      <ConfirmActionModal
        show={approveModal}
        heading="Mark Ready for Activation"
        confirmText="Mark Ready for Activation"
        confirmDisabled={approveDraftAdvanceCount > 0}
        description={
          <>
            <p>
              You are about to mark this AMC ready for activation. Once
              confirmed:
            </p>
            <ul>
              <li>
                The AMC will be marked as{" "}
                <span className="text-primary">READY FOR ACTIVATION</span>.
              </li>
              <li>
                Visits will be automatically scheduled based on the number of
                visits.
              </li>
              <li>
                The AMC can then become{" "}
                <span className="text-primary">ACTIVE</span> on its start date.
              </li>
            </ul>
            <p>
              Please make sure the following information is correct before
              proceeding:
            </p>
            <ul>
              <li>Start Date</li>
              <li>End Date</li>
              <li>Number of visits</li>
              <li>Client Details</li>
              <li>Agreed Amount</li>
            </ul>
            <p>
              Once confirmed, changes to these fields may be restricted to the
              owner.
            </p>
            <div className="mb-0">
              <Label className="form-label" for="amc-approve-final-amount">
                Final amount{" "}
                <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Input
                id="amc-approve-final-amount"
                type="number"
                min={0}
                step="0.01"
                placeholder="Leave blank to bill the items total as-is"
                value={approveFinalAmount}
                onChange={(e) => setApproveFinalAmount(e.target.value)}
              />
            </div>
            {contract?.agreedAmount == null ? (
              <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
                Agreed amount is missing for this contract. Consider adding it
                before proceeding.
              </div>
            ) : null}
            {approveDraftAdvanceCount > 0 ? (
              <div className="alert alert-warning py-2 px-3 mt-3 mb-0 small">
                Cannot proceed: {approveDraftAdvanceCount} advance
                {approveDraftAdvanceCount !== 1 ? "s" : ""} linked to this
                record {approveDraftAdvanceCount !== 1 ? "are" : "is"} still
                awaiting receipt. All advances must be received or cancelled
                first.
              </div>
            ) : null}
            {contract ? (
              <LinkedClientAdvancesTable
                className="mt-3"
                sourceType={ClientAdvanceSourceType.AMC}
                sourceId={contract.id}
                onLoaded={(advances: ClientAdvance[]) =>
                  setApproveDraftAdvanceCount(
                    advances.filter(
                      (a) => a.status === ClientAdvanceStatus.DRAFT,
                    ).length,
                  )
                }
              />
            ) : null}
          </>
        }
        onConfirm={handleApprove}
        onClose={() => setApproveModal(false)}
      />

      <ConfirmActionModal
        iconClass="bx bxs-error-alt"
        iconColor="text-danger"
        show={cancelModal}
        title="Cancel AMC"
        heading="Are you sure you want to cancel this AMC?"
        description={
          <div>
            <p>Cancelling this contract will:</p>
            <ul>
              <li>
                Mark the contract as{" "}
                <span className="text-primary">CANCELLED</span>.
              </li>
              <li>Prevent any further visits from being scheduled.</li>
              <li>
                Record who cancelled the contract and the reason for
                cancellation.
              </li>
              <li>
                Auto-cancel any linked client advance that hasn't been received
                yet. If one has already been received, cancellation will be
                blocked until it's refunded first.
              </li>
            </ul>
            <p>
              Please ensure that cancelling this contract is necessary, as this
              action cannot be undone.
            </p>
            <div className="mb-0">
              <Label className="form-label" for="amc-cancel-reason">
                Reason for cancellation{" "}
                <span className="text-muted fw-normal">(optional)</span>
              </Label>
              <Input
                id="amc-cancel-reason"
                type="textarea"
                rows={3}
                placeholder="This will be recorded for audit purposes…"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
          </div>
        }
        confirmText="Confirm Cancellation"
        confirmColor="danger"
        cancelText="Close"
        onConfirm={handleCancel}
        onClose={() => setCancelModal(false)}
      />

      {/* Complete — confirmation only, no fields */}
      <Modal
        isOpen={completeModal}
        toggle={() => !completeSubmitting && setCompleteModal(false)}
        centered
      >
        <ModalHeader
          toggle={() => !completeSubmitting && setCompleteModal(false)}
        >
          Complete contract
        </ModalHeader>
        <ModalBody>
          <p className="text-muted mb-3">
            This marks the contract as <strong>Completed</strong>. Blocked if
            any linked visit is still planned or in progress.
          </p>
          {contract ? (
            <p className="text-muted small mb-3">
              Agreed amount:{" "}
              <span className="text-body fw-medium">
                {contract.agreedAmount != null
                  ? formatCurrency(contract.agreedAmount)
                  : "—"}
              </span>
              {contract.finalAmount != null ? (
                <>
                  {" "}
                  — final amount:{" "}
                  <span className="text-body fw-medium">
                    {formatCurrency(contract.finalAmount)}
                  </span>
                </>
              ) : null}
            </p>
          ) : null}
          {contract ? (
            <LinkedClientAdvancesTable
              sourceType={ClientAdvanceSourceType.AMC}
              sourceId={contract.id}
            />
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={completeSubmitting}
            onClick={() => setCompleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            color="success"
            disabled={completeSubmitting}
            onClick={() => void handleComplete()}
          >
            {completeSubmitting ? "Please wait…" : "Complete"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Regenerate invoice — cancels the current one, generates a fresh one */}
      <RegenerateInvoiceModal
        show={regenerateModal}
        onClose={() => setRegenerateModal(false)}
        onSuccess={() => void tableRef.current?.()}
        currentFinalAmount={contract?.finalAmount}
        submit={(payload) => {
          if (!contract) throw new Error("No contract selected.");
          return regenerateAmcInvoice(contract.id, payload);
        }}
      />

      <TableContainer
        columns={columns}
        fetchApi={getAmcContracts}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        requestParams={listRequestParams}
        SearchPlaceholder="Search by code, title, client…"
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        toolbarRight={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <AmcListFilters filters={listFilters} onChange={setListFilters} />
            {filtersBadgeCount > 0 && (
              <button
                type="button"
                className="btn btn-soft-danger d-flex align-items-center gap-1"
                onClick={clearListFilters}
              >
                <i className="ri-close-circle-line" />
                Clear
              </button>
            )}
          </div>
        }
      />
    </React.Fragment>
  );
};
